import { useEffect, useMemo, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useSettings from "../../data/useSettings";
import AdminSettingsTable from "../../components/adminSettings/AdminSettingsTable";
import BusinessCurrencyCard from "../../components/adminSettings/BusinessCurrencyCard";
import LoadingWheel from "../../components/shared/LoadingWheel";
import classNames from "../../utils/classNames";
import type { ISetting } from "../../models/Setting";

type TabGroup = {
  tabs: string[];
  grouped: Record<string, ISetting[]>;
};

export default function AdminCredentials() {
  const { setInfo } = usePageNameContext();
  const { saveHandlers } = useDefaultCRUDHandlers("Admin Settings");
  const { generalSettings, loading } = useSettings();

  useEffect(() => {
    setInfo({
      name: "Admin Settings",
      desc: "View and configure your admin settings",
    });
  }, []);

  const { tabs, grouped } = useMemo<TabGroup>(() => {
    const map = new Map<string, ISetting[]>();

    const add = (tab: string, s: ISetting) => {
      if (!map.has(tab)) map.set(tab, []);
      map.get(tab)!.push(s);
    };

    (generalSettings ?? []).forEach((s) => {
      // Part tags are edited in Admin -> Part Tags now; the legacy rows only seed the Default tag.
      if (s.key.startsWith("Tags:")) return;

      // Till:* has its own Admin page (Admin → Till) with proper pickers; listing raw GUIDs here too just confuses.
      if (s.key.startsWith("Till:")) return;
      const idx = s.key.indexOf(":"); // FIRST ':' only
      if (idx === -1) add("General", s);
      else {
        const prefix = s.key.slice(0, idx).trim();
        add(prefix.length ? prefix : "General", s);
      }
    });

    const general = map.get("General") ?? [];
    const prefixes = Array.from(map.keys()).filter((k) => k !== "General").sort((a, b) => a.localeCompare(b));

    // Hide General tab if empty
    const tabList = general.length > 0 ? ["General", ...prefixes] : prefixes;

    // If everything is empty, fall back to a single tab so UI doesn't break
    const finalTabs = tabList.length ? tabList : ["All"];

    if (finalTabs[0] === "All") {
      map.set("All", generalSettings ?? []);
    }

    return {
      tabs: finalTabs,
      grouped: Object.fromEntries(map.entries()) as Record<string, ISetting[]>,
    };
  }, [generalSettings]);

  const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    setActiveTab((prev) => (tabs.includes(prev) ? prev : tabs[0]));
  }, [tabs]);

  if (loading) {
    return (
      <div className="p-6">
        <LoadingWheel />
      </div>
    );
  }

  const filteredSettings = grouped[activeTab] ?? [];

  return (
    <div className="bg-white rounded-lg shadow h-full">
      <div className="min-h-[256px] divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x h-full">
        {/* Left sub-menu */}
        <aside className="py-6 lg:col-span-2">
          <nav className="space-y-1">
            {tabs.map((t) => {
              const current = t === activeTab;

              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setActiveTab(t)}
                  className={classNames(
                    current
                      ? "bg-primary-50 border-primary-500 text-primary-700 hover:bg-primary-50 hover:text-primary-700"
                      : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                    "w-full text-left group border-l-4 px-3 py-2 flex items-center text-sm font-medium"
                  )}
                >
                  <span className="truncate">{t}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right content */}
        <div className="bg-gray-50 rounded-r-lg lg:col-span-10">
          <div className="h-full py-6 px-4 sm:p-6 lg:pb-8">
            <BusinessCurrencyCard />
            <AdminSettingsTable settings={filteredSettings} />
          </div>
        </div>
      </div>
    </div>
  );
}
